import { z } from 'zod';
import { cuid, dinheiro, isoDate, paginacao, taxa } from './primitives.js';
import type { RefsCustoFicha } from './fichas-tecnicas.js';
import type { ReformaFormacaoResponse } from './reforma.js';
import type { MarcaRevisaoResponse } from './lotes.js';
import type { OrigemLucro } from './tabelas-preco.js';

export const opcoesSchema = z
  .object({
    /** divisorWarningThreshold (Q2). Fracao. */
    divisorWarningThreshold: taxa.optional(),
  })
  .strict();

/** Fracao com sinal (-0.2 = -20%), ate 8 casas: variacao do custo. */
const fracaoComSinal = z.string().regex(/^-?\d{1,4}(\.\d{1,8})?$/, 'Fracao invalida (ate 8 casas, use ponto)');

/** Maximo de cenarios por requisicao de /precificacao/cenarios (alem da base). */
export const MAX_CENARIOS = 5;

/**
 * Ajustes de ENTRADA do motor para simulacao de cenario (nada disso altera cadastro). Cada campo ausente = valor atual.
 *  - custo / custoVariacao: custo unitario absoluto OU variacao em fracao sobre o custo atual (excludentes).
 *  - despesaVariavel: taxa variavel TOTAL (tributos do produto + despesas da tabela, como em resultado.variableExpenseRate); o IPI segue.
 *  - despesaFixa, taxaFinanceira: substituem os parametros da empresa. lucroDesejado: substitui o da tabela.
 *  - praticado: preco praticado COM IPI a prazo deste cenario.
 */
export const ajustesSchema = z
  .object({
    custo: dinheiro.optional(),
    custoVariacao: fracaoComSinal.optional(),
    despesaVariavel: taxa.optional(),
    despesaFixa: taxa.optional(),
    lucroDesejado: taxa.optional(),
    taxaFinanceira: taxa.optional(),
    praticado: dinheiro.optional(),
  })
  .strict()
  .refine((a) => a.custo === undefined || a.custoVariacao === undefined, {
    message: 'Informe custo OU custoVariacao, nao os dois',
    path: ['custoVariacao'],
  });
export type AjustesCenario = z.infer<typeof ajustesSchema>;

/** Entrada comum de /precificacao/simular e /precificacao. Dinheiro e taxa: string decimal. */
export const precificarSchema = z.object({
  itemId: cuid,
  tabelaId: cuid,
  condicaoId: cuid,
  /** Data da operacao: resolve vigencias de tributos, tabela e parametros. Default: hoje (UTC). */
  data: isoDate.optional(),
  /** Preco praticado COM IPI, a prazo (9.5.2). Ausente ou 0 = sem simulacao de lucro. */
  praticado: dinheiro.optional(),
  /** Custo unitario avulso (substitui a ficha escolhida; formacao grava fichaId nulo). */
  custoAvulso: dinheiro.optional(),
  /**
   * Sessao 37: SIMULA com outra ficha tecnica do item (versao que nao e a ativa). So simulacao: gravar a formacao, o praticado, a liberacao ou a
   * publicacao com uma ficha que nao e a ativa responde 422 `FICHA_NAO_ATIVA`. Ausente = a ficha ativa (escolhida).
   */
  fichaId: cuid.optional(),
  options: opcoesSchema.optional(),
  /** Ajustes de cenario sobre a entrada do motor (simulador). Gravados no snapshot se a formacao for gravada. */
  ajustes: ajustesSchema.optional(),
});
export type PrecificarDto = z.infer<typeof precificarSchema>;

/**
 * GET /precificacao/item-custo (Sessao 37): o custo do produto/servico e a composicao ANTES de escolher tabela e condicao (a Formacao mostra assim que
 * o produto e escolhido), com as fichas do item para o seletor. `fichaId` calcula com outra versao (so exibicao; nao grava nada).
 */
export const itemCustoQuerySchema = z.object({ itemId: cuid, data: isoDate.optional(), fichaId: cuid.optional() });
export type ItemCustoQuery = z.infer<typeof itemCustoQuerySchema>;

export type ItemCustoOrigem = 'FICHA' | 'COMPOSICAO' | 'AQUISICAO' | 'SEM_CUSTO' | 'ERRO';
export type ItemCustoMotivo = 'SEM_FICHA_ATIVA' | 'SEM_AQUISICAO' | 'TIPO_SEM_CUSTO' | 'ERRO_FICHA';

export interface ItemCustoFichaResumo {
  id: string;
  versao: number;
  descricao: string | null;
  /** E a ficha ativa (escolhida) do item. */
  ativa: boolean;
}

export interface ItemCustoResponse {
  itemId: string;
  codigo: string;
  descricao: string;
  tipo: string;
  data: string;
  origem: ItemCustoOrigem;
  /** Presente quando `origem` e SEM_CUSTO ou ERRO. */
  motivo?: ItemCustoMotivo;
  /** Mensagem da API para `ERRO` (ciclo na cascata, centro sem CPM...). */
  mensagem?: string;
  /** Fichas do item (mais nova primeiro): alimenta o seletor de ficha da Formacao. Vazio em item comprado. */
  fichas: ItemCustoFichaResumo[];
  /** Ficha ativa (escolhida) do item, ou null. */
  fichaAtiva: ItemCustoFichaResumo | null;
  /** Ficha usada no calculo (a ativa, ou a pedida em `fichaId`). */
  fichaUsada: ItemCustoFichaResumo | null;
  /** Custo unitario na data (string decimal, 6 casas). Nulo sem custo. */
  custoUnitario: string | null;
  /** Composicao do custo da ficha (so FICHA/COMPOSICAO). */
  composicao: { materiais: string; processo: string; semiacabados: string; total: string } | null;
}

/**
 * POST /precificacao/reverso (Sessao 16): "quero X R$ de lucro / de MC" -> preco. `valor` e por unidade (`base = UNIDADE`) ou no total da
 * quantidade (`base = TOTAL`, exige `quantidade`; a API divide por ela). Aceita os ajustes do simulador. Nao grava.
 */
export const reversoSchema = precificarSchema
  .omit({ praticado: true })
  .extend({
    alvo: z.enum(['LUCRO_RS', 'MC_RS']),
    valor: dinheiro,
    base: z.enum(['UNIDADE', 'TOTAL']).default('UNIDADE'),
    quantidade: dinheiro.optional(),
  })
  .refine((v) => v.base === 'UNIDADE' || (v.quantidade !== undefined && Number(v.quantidade) > 0), { message: 'Informe a quantidade (maior que zero) para o alvo no total', path: ['quantidade'] });
export type ReversoDto = z.infer<typeof reversoSchema>;

/** POST /precificacao/cenarios: um item x tabela x condicao e ate MAX_CENARIOS variacoes. Nao grava. */
export const cenariosSchema = z.object({
  itemId: cuid,
  tabelaId: cuid,
  condicaoId: cuid,
  /** Sessao 37: simula com outra ficha do item (so exibicao). */
  fichaId: cuid.optional(),
  data: isoDate.optional(),
  /** Praticado comum a todos os cenarios (cada um pode trazer o seu em ajustes.praticado). */
  praticado: dinheiro.optional(),
  custoAvulso: dinheiro.optional(),
  options: opcoesSchema.optional(),
  cenarios: z
    .array(z.object({ nome: z.string().trim().min(1).max(60).optional(), ajustes: ajustesSchema }))
    .min(1)
    .max(MAX_CENARIOS),
});
export type CenariosDto = z.infer<typeof cenariosSchema>;

export const listarFormacoesSchema = paginacao.extend({ itemId: cuid.optional() });
export type ListarFormacoesQuery = z.infer<typeof listarFormacoesSchema>;

/** Parte do preco (sem IPI, a vista): fracao (4 casas) e valor (2 casas), strings decimais prontas para exibir. */
export interface SegmentoComposicaoResponse {
  fracao: string;
  valor: string;
}

/**
 * Equacao do preco e "para onde vai cada real", derivada na API do resultado do motor e JA ARREDONDADA para exibir
 * (dinheiro 2 casas, markup e fracoes 4 casas). O resultado de precisao cheia segue em `resultado`.
 */
export interface EquacaoPrecoResponse {
  custoUnitario: string;
  /** Preco a vista COM IPI / custo (markup sobre o custo, IPI incluso). Nulo com custo zero. */
  markup: string | null;
  /** Markup sem IPI (praticado: preco liquido / custo). */
  markupSemIpi: string | null;
  /** Preco a vista com IPI. */
  aVista: string;
  /** Preco a prazo com IPI menos a vista com IPI. */
  custoFinanceiro: string;
  /** Preco a prazo com IPI (sugerido ou praticado). */
  preco: string;
  semIpi: string;
  ipi: string;
  /** Taxa financeira do prazo (fator - 1). */
  taxaFinanceira: string;
  /** Lucro efetivo a prazo (fracao). */
  margemEfetivaAPrazo: string;
  /** Segmentos do preco a vista sem IPI. */
  segmentos: {
    custo: SegmentoComposicaoResponse;
    despesaVariavel: SegmentoComposicaoResponse;
    despesaFixa: SegmentoComposicaoResponse;
    lucro: SegmentoComposicaoResponse;
  };
}

export interface ComposicaoPrecoResponse {
  sugerido: EquacaoPrecoResponse;
  /** Presente so com praticado > 0. */
  praticado: (EquacaoPrecoResponse & { markupSobreSugerido: string | null }) | null;
  /** Lucro minimo da tabela (fracao); o desejado esta em resultado.desiredProfitRate. */
  lucroMinimo: string | null;
  /** Despesas variaveis da tabela usadas pelo motor (T = tributo, C = comissao, O = outra); taxas em fracao, nas casas da empresa. */
  despesasVariaveis?: { tipo: 'T' | 'C' | 'O'; descricao: string; taxa: string; /** R$ da linha no preco sugerido (taxa x base do cartao). */ valorSugerido?: string; /** R$ da linha no praticado (so com praticado). */ valorPraticado?: string }[];
  /** Taxa total das despesas variaveis (a do resultado do motor). */
  despesasVariaveisTotal?: string;
}

/** Faixa da margem de contribuicao: RED = prejuizo marginal (MC <= 0); ORANGE = nao cobre a despesa fixa; GREEN = cobre. */
export type FaixaMc = 'GREEN' | 'ORANGE' | 'RED';

/** Decomposicao da MC de um preco (R$ por unidade). So com custo.ver; sem ele a API manda so `mcPct` e `faixa` (ou so a faixa). */
export interface McBlocoResponse {
  precoSemIpi: string;
  despesasVariaveis: string;
  custoFinanceiro: string;
  custoVariavel: string;
  mc: string;
  /** Fracao (MC / preco sem IPI); null se o preco e zero. */
  mcPct: string | null;
  faixa: FaixaMc;
}

/** Margem de contribuicao na Formacao: sugerido e praticado, e como o custo foi dividido (variavel x processo absorvido como fixo). */
export interface MargemContribuicaoResponse {
  /** Parametro "processo e custo variavel" vigente na data. */
  processoCustoVariavel: boolean;
  /** Custo de processo (minutos x CPM) tratado como fixo absorvido (0 se o parametro e sim). */
  custoProcessoAbsorvido: string;
  sugerido: McBlocoResponse;
  praticado: McBlocoResponse | null;
}

export interface FormacaoCalculadaResponse {
  referenciaEm: string;
  motorVersao: string;
  /** Entrada exata do motor + options + refs (o que seria gravado). */
  snapshot: unknown;
  resultado: unknown;
  warnings: unknown[];
  /** Equacao e composicao do preco (so em /precificacao/simular e /precificacao). */
  composicao?: ComposicaoPrecoResponse;
  /** De onde veio o lucro desejado/minimo entregue ao motor (DP6): da tabela ou do padrao da empresa. */
  lucroDesejadoOrigem: OrigemLucro;
  lucroMinimoOrigem: OrigemLucro;
  /**
   * Custo da ficha na data usado no `unitCost` (etapas com o CPM e a competencia, semiacabados em cascata, marca
   * "CPM antigo (mes)"); o mesmo que `snapshot.refs.custoFicha`. Null com custo avulso ou custo de aquisicao.
   */
  custoFicha: RefsCustoFicha | null;
  /** Marca aberta "custo atualizado - revisar preco" desta chave (item x tabela x condicao), ao vivo; null quando nao ha. */
  marcaRevisao?: MarcaRevisaoResponse | null;
  /**
   * Sessao 37: faixa do lucro do PRATICADO contra a meta e o minimo, decidida pelo motor com a tolerancia de meio centavo no preco (green/orange/red);
   * ausente sem praticado. A tela usa esta faixa em vez de comparar os percentuais.
   */
  faixaPraticado?: 'green' | 'orange' | 'red';
  /** Sessao 15: margem de contribuicao do sugerido e do praticado. */
  margemContribuicao?: MargemContribuicaoResponse;
  /** Sessao 15 (6b): regime da reforma pela data da operacao, tributos por fora e total da NF. */
  reforma?: ReformaFormacaoResponse;
}

export interface FormacaoResponse extends FormacaoCalculadaResponse {
  id: string;
  itemId: string;
  vigenciaId: string;
  condicaoId: string;
  fichaId: string | null;
  unitCost: string;
  variableExpenseRate: string;
  financialFactor: string;
  ipiRate: string;
  precoSemIpiAVista: string;
  precoSemIpi: string;
  precoComIpi: string;
  practicedPrice: string | null;
  simulatedProfit: string | null;
  createdAt: string;
}

export interface RecalculoResponse {
  id: string;
  motorVersao: string;
  /** true se priceFormation(snapshot) reproduz exatamente o resultado gravado. */
  reproduz: boolean;
  resultado: unknown;
}

/** Diferencas de um cenario contra a base, calculadas na API e ja arredondadas para exibir. Strings com sinal ("-1.23"). */
export interface DiferencasCenarioResponse {
  /** Preco sugerido (com IPI, a prazo) do cenario menos o da base. Dinheiro, 2 casas. */
  sugerido: string;
  /** Variacao relativa do sugerido sobre a base (fracao, 4 casas). Nulo se a base e zero. */
  variacaoSugerido: string | null;
  /** Lucro simulado no praticado, cenario menos base (fracao, 4 casas). Nulo se algum dos dois nao tem praticado. */
  lucroSimulado: string | null;
  /** Margem efetiva a prazo do sugerido, cenario menos base (fracao, 4 casas). */
  margemEfetivaAPrazo: string;
}

export interface CenarioResponse {
  nome: string;
  ajustes: AjustesCenario;
  /** Preenchidos quando o motor calculou; senao `erro` (ex.: DIVISOR_INVALIDO) e os demais ficam nulos. */
  resultado: unknown;
  warnings: unknown[];
  composicao: ComposicaoPrecoResponse | null;
  /** Nulo na base. */
  diferencas: DiferencasCenarioResponse | null;
  erro: { code: string; message: string } | null;
}

export interface CenariosResponse {
  referenciaEm: string;
  motorVersao: string;
  /** Entrada do motor da base (sem ajustes): custo, taxas e lucro desejado atuais. */
  snapshot: unknown;
  base: CenarioResponse;
  cenarios: CenarioResponse[];
}

export interface ReversoResponse {
  alvo: 'LUCRO_RS' | 'MC_RS';
  base: 'UNIDADE' | 'TOTAL';
  quantidade: string | null;
  /** O alvo por unidade (valor / quantidade no modo TOTAL). */
  valorPorUnidade: string;
  /** Preco exato pedido pelo alvo (sem arredondar), na base do praticado (com IPI, a prazo). */
  preco: string;
  /** O mesmo preco nas casas de dinheiro da empresa: e o que a formacao abaixo simulou como praticado. */
  precoArredondado: string;
  /** Formacao com `praticado = precoArredondado` (lucro e MC obtidos de fato, composicao, reforma). */
  formacao: FormacaoCalculadaResponse;
}
